import { copyStringToClipboard } from '@/lib/copyToClipboard';
import { Ionicons } from '@expo/vector-icons';
import React, { useCallback, useState } from 'react';
import {
  Platform,
  Pressable,
  TextInput,
  View,
} from 'react-native';
import {
  Dialog,
  SizableText,
  VisuallyHidden,
  XStack,
  YStack,
} from 'tamagui';
import { BodyText, SectionTitle, SubtitleText } from '@/components/ui/Typography';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shareUrl: string;
  shareAtCurrentTime: boolean;
  onShareAtCurrentTimeChange: (value: boolean) => void;
  onCopied: () => void;
  onCopyError: () => void;
};

export function LessonShareModal({
  open,
  onOpenChange,
  shareUrl,
  shareAtCurrentTime,
  onShareAtCurrentTimeChange,
  onCopied,
  onCopyError,
}: Props) {
  const [copyBusy, setCopyBusy] = useState(false);

  const handleCopy = useCallback(async () => {
    if (!shareUrl.trim() || copyBusy) return;
    setCopyBusy(true);
    try {
      await copyStringToClipboard(shareUrl);
      onCopied();
    } catch {
      onCopyError();
    } finally {
      setCopyBusy(false);
    }
  }, [shareUrl, copyBusy, onCopied, onCopyError]);

  if (!open) return null;

  return (
    <Dialog modal open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal zIndex={200}>
        <Dialog.Overlay
          key="share-overlay"
          animation="quick"
          opacity={0.45}
          backgroundColor="rgba(0, 0, 0, 0.45)"
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
          onPress={() => onOpenChange(false)}
          zIndex={200}
        />
        <Dialog.Content
          elevate
          key="share-content"
          animation="quick"
          enterStyle={{ opacity: 0, scale: 0.96 }}
          exitStyle={{ opacity: 0, scale: 0.98 }}
          backgroundColor="#FFFFFF"
          borderRadius={16}
          padding={24}
          width="94%"
          maxWidth={600}
          gap={20}
          zIndex={201}
          onPress={(e) => e.stopPropagation()}
        >
          <VisuallyHidden>
            <Dialog.Title>Share</Dialog.Title>
          </VisuallyHidden>

          <XStack jc="space-between" ai="center" width="100%">
            <SectionTitle color="#0D0D0D">
              Share
            </SectionTitle>
            <Pressable
              onPress={() => onOpenChange(false)}
              accessibilityRole="button"
              accessibilityLabel="Close"
              hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
              style={
                Platform.OS === 'web'
                  ? ({ cursor: 'pointer' } as object)
                  : undefined
              }
            >
              <Ionicons name="close" size={26} color="#0D0D0D" />
            </Pressable>
          </XStack>

          <YStack gap={12} width="100%">
            <XStack ai="center" gap={8}>
              <Ionicons name="link-outline" size={18} color="#0D0D0D" />
              <SubtitleText color="#0D0D0D">
                Video URL
              </SubtitleText>
            </XStack>

            <XStack
              ai="center"
              borderWidth={1}
              borderColor="#C9C5C3"
              borderRadius={10}
              overflow="hidden"
              backgroundColor="#FAFAFA"
              width="100%"
              paddingLeft={12}
              paddingRight={6}
              paddingVertical={6}
              gap={8}
            >
              <TextInput
                value={shareUrl}
                editable={false}
                multiline={false}
                selectTextOnFocus={Platform.OS === 'web'}
                style={{
                  flex: 1,
                  fontSize: 14,
                  color: '#0D0D0D',
                  paddingVertical: 6,
                  ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as object) : {}),
                }}
              />
              <Pressable
                onPress={handleCopy}
                disabled={!shareUrl.trim() || copyBusy}
                accessibilityRole="button"
                accessibilityLabel="Copy link"
                style={{
                  backgroundColor: '#0D0D0D',
                  paddingHorizontal: 16,
                  paddingVertical: 8,
                  borderRadius: 999,
                  opacity: !shareUrl.trim() ? 0.45 : 1,
                  ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as object) : {}),
                }}
              >
                <SizableText color="#FFFFFF" fontWeight="600" fontSize={14}>
                  Copy
                </SizableText>
              </Pressable>
            </XStack>
          </YStack>

          <YStack gap={14} width="100%">
            <CheckboxRow
              label="Share at current time"
              checked={shareAtCurrentTime}
              onToggle={() => onShareAtCurrentTimeChange(!shareAtCurrentTime)}
            />
          </YStack>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
}

function CheckboxRow({
  label,
  checked,
  onToggle,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <Pressable
      onPress={onToggle}
      accessibilityRole="checkbox"
      accessibilityState={{ checked }}
      style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}
    >
      <View
        style={{
          width: 20,
          height: 20,
          borderRadius: 4,
          borderWidth: 1.5,
          borderColor: '#A8A8A8',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: checked ? '#0D0D0D' : '#FFFFFF',
        }}
      >
        {checked ? (
          <Ionicons name="checkmark" size={14} color="#FFFFFF" />
        ) : null}
      </View>
      <BodyText flex={1} color="#5C5854" fontWeight="500">
        {label}
      </BodyText>
    </Pressable>
  );
}
